import styles from './MultiNumberRange.module.scss';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DictionaryContext } from 'application/adapters/context/Dictionary';

export interface MultiNumberRangeProps {
	className?: string;
	min: number;
	max: number;
	callback: (value: [number, number]) => void;
}

export const MultiNumberRange: React.FC<MultiNumberRangeProps> = ({ className, min, max, callback }) => {
	const [value, setValue] = useState<[number, number]>([min, max]);
	const minValRef = useRef(min);
	const maxValRef = useRef(max);
	const minInput = useRef<HTMLInputElement>(null);
	const range = useRef<HTMLDivElement>(null);

	const dictionary = useContext(DictionaryContext);

	// Converts to percentage
	const getPercent = useCallback((value: number) => Math.round(((value - min) / (max - min)) * 100), [min, max]);

	// Sets width of the range to decrease from the left side
	useEffect(() => {
		minInput.current.style.zIndex = '5';
		const minPercent = getPercent(value[0]);
		const maxPercent = getPercent(maxValRef.current);

		if (range.current) {
			range.current.style.left = `${minPercent}%`;
			range.current.style.width = `${maxPercent - minPercent}%`;
		}
	}, [value, getPercent]);

	// Sets width of the range to decrease from the right side
	useEffect(() => {
		minInput.current.style.zIndex = '4';
		const minPercent = getPercent(minValRef.current);
		const maxPercent = getPercent(value[1]);

		if (range.current) {
			range.current.style.width = `${maxPercent - minPercent}%`;
		}
	}, [value[1], getPercent]);

	const handleChange = (type, event) => {
		const currentValue = [...value] as [number, number];

		if (type === 'min') {
			const minVal = Math.min(Number(event.target.value), currentValue[1] - 1);
			currentValue[0] = minVal;
			minValRef.current = minVal;
		}

		if (type === 'max') {
			const maxVal = Math.max(Number(event.target.value), currentValue[0] + 1);
			currentValue[1] = maxVal;
			maxValRef.current = maxVal;
		}

		setValue(currentValue);
		callback(currentValue);
	};

	return (
		<fieldset className={classNames(styles.MultiNumberRange, className)}>
			<legend className={styles.MultiNumberRange_label}>{dictionary.getValue('Price', null, 'Price')}</legend>
			<input
				type="range"
				min={min}
				max={max}
				step="50"
				value={value[0]}
				onChange={(event) => handleChange('min', event)}
				className={classNames(styles.MultiNumberRange_thumb, styles.MultiNumberRange_thumb___left)}
				ref={minInput}
				aria-label="Minimum"
			/>
			<input
				type="range"
				min={min}
				max={max}
				step="50"
				value={value[1]}
				onChange={(event) => handleChange('max', event)}
				className={classNames(styles.MultiNumberRange_thumb, styles.MultiNumberRange_thumb___right)}
				aria-label="Maximum"
			/>

			<div className={styles.MultiNumberRange_slider}>
				<div className={styles.MultiNumberRange_sliderTrack}></div>
				<div ref={range} className={styles.MultiNumberRange_sliderRange}></div>
				<div className={styles.MultiNumberRange_sliderLeftValue}>
					{value[0]} {dictionary.getValue('Price.Currency', null, 'DKK')}
				</div>
				<div className={styles.MultiNumberRange_sliderRightValue}>
					{value[1]} {dictionary.getValue('Price.Currency', null, 'DKK')}
				</div>
			</div>
		</fieldset>
	);
};
